import { Field } from '@base-ui/react/field'
import { forwardRef, type FC, type HTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import styles from './select.module.css'

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  fullWidth?: boolean
  /**
   * @deprecated Ignored: Select always renders outlined (GH #2396). It used to reach the native `<select>` as an
   * unknown attribute and change nothing; nothing in the app asks for another variant.
   */
  variant?: 'outlined' | 'standard' | 'filled'
  displayEmpty?: boolean
  renderValue?: (value: unknown) => ReactNode
  error?: boolean
  helperText?: string
}

/**
 * Select primitive using a native <select> element wrapped with Base UI Field
 * for proper label-input association and accessibility.
 *
 * NOTE: Base UI provides a custom Select component (`@base-ui/react/select`)
 * with keyboard navigation and a styled dropdown. Migrating to it would significantly
 * change the API (value type, children structure, trigger/popup pattern). Consider
 * migrating to Base UI Select for new code that needs a custom dropdown experience.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, fullWidth: _fullWidth, variant: _variant, error, helperText, className, id, ...rest }, ref) => {
    const wrapperClasses = [styles.textField, className].filter(Boolean).join(' ')

    const selectClasses = [styles.select, error ? styles.error : undefined].filter(Boolean).join(' ')

    return (
      <Field.Root className={wrapperClasses} invalid={error}>
        {label ? <Field.Label className={styles.inputLabel}>{label}</Field.Label> : null}
        <div className={styles.selectWrapper}>
          <Field.Control render={<select ref={ref} id={id} className={selectClasses} {...rest} />} />
          <span className={styles.arrow} aria-hidden="true" />
        </div>
        {helperText ? (
          error ? (
            <Field.Error className={[styles.helperText, styles.helperTextError].join(' ')} match>
              {helperText}
            </Field.Error>
          ) : (
            <Field.Description className={styles.helperText}>{helperText}</Field.Description>
          )
        ) : null}
      </Field.Root>
    )
  }
)

Select.displayName = 'Select'

export interface FormControlProps extends HTMLAttributes<HTMLDivElement> {
  fullWidth?: boolean
  error?: boolean
  disabled?: boolean
}

export const FormControl: FC<FormControlProps> = ({
  fullWidth: _fullWidth,
  error: _error,
  disabled,
  className,
  style,
  ...rest
}) => {
  const classes = [styles.formControl, className].filter(Boolean).join(' ')

  return (
    <div className={classes} style={disabled ? { opacity: 0.38, pointerEvents: 'none', ...style } : style} {...rest} />
  )
}

export interface InputLabelProps extends HTMLAttributes<HTMLLabelElement> {
  htmlFor?: string
}

export const InputLabel: FC<InputLabelProps> = ({ className, ...rest }) => (
  <label className={[styles.inputLabel, className].filter(Boolean).join(' ')} {...rest} />
)

export interface FormLabelProps extends HTMLAttributes<HTMLLabelElement> {
  htmlFor?: string
}

export const FormLabel: FC<FormLabelProps> = ({ className, ...rest }) => (
  <label className={[styles.formLabel, className].filter(Boolean).join(' ')} {...rest} />
)
